import { Game } from '@Game/Game';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { TButtonState } from '@GFTypes/GameStoreTypes/ButtonState';
import { EWinType } from '@Managers/Redux/HttpState';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { applyButtonBlockers } from './ButtonBlockers';

const updateButtonState = (game: Game, newState: Partial<TButtonState>): void => {
    const buttonState = game.gameStore.get(EGameStoreKeys.ButtonState);
    applyButtonBlockers(game, newState);
    game.gameStore.set(EGameStoreKeys.ButtonState, {
        ...buttonState,
        ...newState
    });
};

export const onRefreshBalanceButtonRule = (game: Game): void => {
    updateButtonState(game, {});
};

export const onIdleButtonRule = (game: Game): void => {
    const isSpecialGame = game.gameStore.get(EGameStoreKeys.IsLockAndRollMode) || game.gameStore.get(EGameStoreKeys.IsFreeGamesMode);
    updateButtonState(game, {
        isBetDecreaseButtonActive: true,
        isBetIncreaseButtonActive: true,
        isSpinButtonActive: true,
        isSkipButtonActive: false,
        isStopButtonActive: false,
        isTakeButtonActive: false,
        isAutoButtonActive: true,
        isGambleButtonActive: false,
        isTurboButtonActive: true,
        isMenuButtonActive: true,
        isBonusBuyButtonActive: !isSpecialGame
    });
};

export const onPreSpinButtonRule = (game: Game): void => {
    updateButtonState(game, {
        isBetDecreaseButtonActive: false,
        isBetIncreaseButtonActive: false,
        isGambleButtonActive: false,
        isSpinButtonActive: false,
        isTakeButtonActive: false,
        isStopButtonActive: true,
        isAutoButtonActive: true,
        isBonusBuyButtonActive: false
    });
};

export const onWinStartButtonRule = (game: Game): void => {
    const isAuto = game.gameStore.get(EGameStoreKeys.AutoPlayState).isEnabled;
    updateButtonState(game, {
        isBetDecreaseButtonActive: true,
        isBetIncreaseButtonActive: true,
        isStopButtonActive: false,
        isAutoButtonActive: isAuto,
        isSpinButtonActive: false,
        isSkipButtonActive: true,
        isTakeButtonActive: true,
        isBonusBuyButtonActive: false,
        isGambleButtonActive: !isAuto
    });
};

export const onWinStateButtonRule = (game: Game): void => {
    const { isLastWinPresentationStep } = game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
    if (isLastWinPresentationStep) return onWinStartButtonRule(game);
    updateButtonState(game, {
        isTakeButtonActive: false,
        isSkipButtonActive: true
    });
};

export const onWinEndButtonRule = (game: Game): void => {
    const isBigWin = game.gameStore.get(EGameStoreKeys.BaseWinPresentationState)?.finalWinLevelName !== EWinType.NORMAL_WIN;
    updateButtonState(game, { isSpinButtonActive: !isBigWin, isSkipButtonActive: isBigWin });
};

export const onCompleteWinPresentation = (game: Game): void => {
    const currentState = game.gameStore.get(EGameStoreKeys.CurrentGameState);
    const isDuringWinStates = [EGameStates.WIN_STATE, EGameStates.POST_WIN_STATE].includes(currentState);
    if (isDuringWinStates) updateButtonState(game, { isSpinButtonActive: true, isSkipButtonActive: false });
};

export const onGambleStartButtonRule = (game: Game): void => {
    updateButtonState(game, { isGambleButtonActive: false, isSpinButtonActive: false });
};

export const onGambleButtonRule = (game: Game): void => {
    updateButtonState(game, { isTakeButtonActive: true });
};

export const onBonusBuyOpenButtonRule = (game: Game): void => {
    game.gameStore.set(EGameStoreKeys.BonusBuy, { isDialogOpen: true }, true);
    updateButtonState(game, { isBonusBuyButtonActive: false });
};

export const onBonusBuyCloseButtonRule = (game: Game): void => {
    game.gameStore.set(EGameStoreKeys.BonusBuy, { isDialogOpen: false }, true);
    updateButtonState(game, { isBonusBuyButtonActive: true });
    onIdleButtonRule(game);
};

export const onGambleResultButtonRule = (game: Game): void => {
    updateButtonState(game, { isTakeButtonActive: false });
};

export const onGambleEndButtonRule = (game: Game): void => {
    updateButtonState(game, { isTakeButtonActive: false, isSpinButtonActive: true });
};

export const onTakeButtonRule = (game: Game): void => {
    updateButtonState(game, {
        isStopButtonActive: false,
        isTakeButtonActive: false,
        isGambleButtonActive: false,
        isAutoButtonActive: true
    });
};

export const onWildWheelStartButtonRule = (game: Game): void => {
    updateButtonState(game, {
        isStopButtonActive: false,
        isTakeButtonActive: false,
        isGambleButtonActive: false,
        isAutoButtonActive: true,
        isSkipButtonActive: true
    });
};

export const onWildWheelEndButtonRule = (game: Game): void => {
    const isAuto = game.gameStore.get(EGameStoreKeys.AutoPlayState).isEnabled;
    updateButtonState(game, {
        isStopButtonActive: false,
        isTakeButtonActive: true,
        isGambleButtonActive: !isAuto,
        isAutoButtonActive: isAuto,
        isSkipButtonActive: true
    });
};

export const onMuteButtonRule = (game: Game): void => {
    updateButtonState(game, { isMuteButtonActive: false, isUnmuteButtonActive: true });
};

export const onUnmuteButtonRule = (game: Game): void => {
    updateButtonState(game, { isUnmuteButtonActive: false, isMuteButtonActive: true });
};

export const onPauseButtonRule = (game: Game): void => {
    game.gameStore.set(EGameStoreKeys.IsUIBlocked, true);
    updateButtonState(game, {});
};

export const onUnpauseButtonRule = (game: Game): void => {
    game.gameStore.set(EGameStoreKeys.IsUIBlocked, false);
    onIdleButtonRule(game);
};

export const onPreIntroRule = (game: Game): void => {
    updateButtonState(game, { isContinueButtonActive: true, isSpinButtonActive: false, isSkipButtonActive: false, isStopButtonActive: false });
};

export const onPostIntroRule = (game: Game): void => {
    updateButtonState(game, { isContinueButtonActive: false });
};

export const onPostRetriggerRule = (game: Game): void => {
    updateButtonState(game, { isContinueButtonActive: false });
};

export const onPreOutroRule = (game: Game): void => {
    updateButtonState(game, { isContinueButtonActive: true, isSpinButtonActive: false, isSkipButtonActive: false, isStopButtonActive: false });
};

export const onPostOutroRule = (game: Game): void => {
    updateButtonState(game, { isContinueButtonActive: false });
};
